import { TextAreaStyled, TextFieldStyled } from '@nike/nike-design-system-components';
import { CaretDown, CaretUp } from '@nike/nike-design-system-icons';
import { isSome, match, Default } from '@nike/rcf-fp';
import Big from 'big.js';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { useTenders } from './hooks/PickupProvider';
import PickupDeclarationModal from './PickupDeclarationModal';
import { getSchema, getVarianceReason } from './validation';
import { useGetStoreInfo, useGetLangLocale } from '../../../../globals';
import { useGetLocalization } from '../../../../hooks';
import { getFormattedCurrency, fixedDecimalCount } from '../../../../utils/currencyUtils';
import {
  ID_ACTUAL_AMOUNT,
  ID_VARIANCE_EXPLANATION,
  ID_VARIANCE_REASONS,
} from '../../../../utils/dom-element-ids';
import { getDirection } from '../../../../utils/getDirection';
import { getMaxAmountByCurrency } from '../../../../utils/getMaxAllowableAmount';
import {
  ErrorMessage,
  MultipleCurrencyInput,
  MultipleCurrencyText,
  Table,
  VarianceReasonModal,
} from '../../../reusable';

import '../../../reusable/infoIconRow.css';
import './textFieldStyledReadOnlyOverride.css';

const getAmountByKey = (key) => (item) => fixedDecimalCount(new Big(item[key]).toNumber());
const getActualAmount = getAmountByKey('actualAmount');
const getCurrentFloat = getAmountByKey('currentFloat');

const getTenderType = (tenders) => (props) => {
  const { index } = props;
  const { tenderType, subTenderType } = tenders[index];
  return subTenderType ? `${tenderType}-${subTenderType}` : tenderType;
};

const getExpectedAmount = (tender) => fixedDecimalCount(tender.expectedPickupAmount);

const getPickupAmountForClosedItem =
  (tenders) =>
  ({ index }) => {
    return match(tenders[index])(
      [({ isEditable }) => isEditable, getActualAmount],
      [({ isDisabled }) => isDisabled, getCurrentFloat],
      [Default, fixedDecimalCount(Number(0))],
    );
  };

const getExpectedAmountForClosedItem =
  (tenders) =>
  ({ index }) =>
    getExpectedAmount(tenders[index]);

function ShowFloat({ tender }) {
  const {
    isForeignTender,
    primaryCurrency,
    currentFloat,
    defaultFloat,
    foreignAmount,
    subTenderType,
  } = tender;
  const getLocalization = useGetLocalization();

  return isForeignTender ? (
    <>
      <span className="text-color-secondary">{getLocalization('lbl.foreign-amount')}:</span>{' '}
      <MultipleCurrencyText
        isForeignTender={isForeignTender}
        foreignCurrency={subTenderType}
        value={foreignAmount}
      />
    </>
  ) : (
    <>
      <span className="text-color-secondary">
        {primaryCurrency
          ? getLocalization('lbl.default-float')
          : getLocalization('lbl.current-float')}
        :
      </span>{' '}
      <MultipleCurrencyText value={primaryCurrency ? defaultFloat : currentFloat} />
    </>
  );
}

ShowFloat.propTypes = {
  tender: PropTypes.shape({
    isForeignTender: PropTypes.bool,
    primaryCurrency: PropTypes.bool,
    currentFloat: PropTypes.number,
    defaultFloat: PropTypes.number,
    foreignAmount: PropTypes.number,
    subTenderType: PropTypes.string,
  }).isRequired,
};

function ShowVariance({ tender, variance }) {
  const { isForeignTender, subTenderType } = tender;
  const getLocalization = useGetLocalization();
  return isForeignTender ? (
    <>
      <span className="text-color-secondary">{getLocalization('lbl.variance')}:</span>{' '}
      <MultipleCurrencyText
        isForeignTender={isForeignTender}
        foreignCurrency={subTenderType}
        value={variance}
      />
    </>
  ) : (
    <>
      <span className="text-color-secondary">{getLocalization('lbl.variance')}:</span>{' '}
      <MultipleCurrencyText value={variance} />
    </>
  );
}

ShowVariance.propTypes = {
  tender: PropTypes.shape({
    isForeignTender: PropTypes.bool,
    subTenderType: PropTypes.string,
  }).isRequired,
  variance: PropTypes.number.isRequired,
};

function OpenTableItem({ item }) {
  const getLocalization = useGetLocalization();
  const { updateTender, tenders } = useTenders();
  const { currency: storeCurrency, locale } = useGetStoreInfo();
  const tender = tenders[item.index];

  const isCashTenderType = tender.tenderType === 'CASH';

  const {
    acceptedLoanAmount,
    actualAmount,
    expectedPickupAmount,
    currentFloat,
    defaultFloat = 0,
    primaryCurrency,
    varianceExplanation,
    isEditable,
    isForeignTender,
    foreignAmount,
    subTenderType,
    validation,
  } = tender;

  const { success, error } = validation;
  const isAcceptedLoan = isCashTenderType && isSome(acceptedLoanAmount) && acceptedLoanAmount > 0;
  const mcuPickupMessage =
    isCashTenderType && !isForeignTender
      ? getLocalization('msg.enter-amount-of-cash-for-pickup')
      : getLocalization('msg.enter-tender-amount');
  const [currency, setCurrency] = useState('');
  const [actualPickupAmount, setActualPickupAmount] = useState(getActualAmount(tender));

  const variance = fixedDecimalCount(Math.abs(actualPickupAmount - expectedPickupAmount));

  const validationConfig = {
    fields: {
      pickupAmount: {
        MAX_AMOUNT: getMaxAmountByCurrency(currency),
        NONNEGATIVE: getLocalization('msg.pickup-amount-must-be-zero-or-positive'),
        MIN: getLocalization('msg.is-a-required-field'),
        MAX: getLocalization('msg.number-exceeds-max'),
      },
    },
    variance: {
      short: {
        VALUE: 'Short',
        LABEL: getLocalization('lbl.variance-short'),
      },
      over: {
        VALUE: 'Over',
        LABEL: getLocalization('lbl.variance-over'),
      },
      none: {
        VALUE: 'No Variance',
        LABEL: getLocalization('lbl.no-variance'),
      },
      negativeTillBalance: {
        VALUE: 'Negative Till Balance',
        LABEL: getLocalization('lbl.variance-negative-till-balance'),
      },
    },
  };

  const validate = getSchema(validationConfig);

  const updateAmount = (newValue) => {
    const { success: validateSuccess, error: validateError } = validate.safeParse({
      actualAmount: Number(newValue),
      currentFloat: Number(currentFloat),
      defaultFloat: Number(defaultFloat),
    });

    setActualPickupAmount(newValue);
    updateTender({
      ...tender,
      actualAmount: parseFloat(newValue),
      validation: { success: validateSuccess, error: validateError },
      variance: parseFloat(variance),
      varianceExplanation: parseFloat(variance) > 0.0 ? varianceExplanation : '',
    });
  };

  const updateVarianceExplanation = ({ target: { value: newValue } }) => {
    updateTender({
      ...tender,
      varianceExplanation: parseFloat(variance) > 0.0 ? newValue : '',
    });
  };

  const fieldErrors = !success && error.flatten().fieldErrors;

  const errorMessage = !success && fieldErrors?.actualAmount[0];
  const errorDisplay = errorMessage ? <ErrorMessage>{errorMessage}</ErrorMessage> : null;

  return (
    <div>
      <div className="mb4-sm">
        <MultipleCurrencyInput
          isReadOnly={!isEditable}
          allowNegativeValue={false}
          currency={!isForeignTender ? currency : subTenderType}
          id={ID_ACTUAL_AMOUNT}
          label={getLocalization('lbl.pickup-amount')}
          message={mcuPickupMessage}
          name={ID_ACTUAL_AMOUNT}
          setAmount={updateAmount}
          setCurrency={setCurrency}
          value={parseFloat(fixedDecimalCount(actualAmount))}
          onChange={({ value: newValue }) => updateAmount(newValue)}
        />
        {isAcceptedLoan && (
          <div className="ta-sm-l text-color-error">
            {getLocalization('msg.template.till-safe-loan-return', {
              AMOUNT: getFormattedCurrency(locale, storeCurrency, acceptedLoanAmount),
            })}
          </div>
        )}
        {isEditable && (
          <>
            <div className="ncss-col-sm-6 mt2-sm pl3-sm ta-sm-l">
              <ShowFloat tender={tender} />
            </div>
            <div className="ncss-col-sm-6 mt2-sm pl3-sm ta-sm-l">
              <ShowVariance tender={tender} variance={variance} />
            </div>
          </>
        )}
        <div className="info-icon-row pb3-sm">
          <TextFieldStyled
            readOnly
            className="color-gray"
            id={ID_VARIANCE_REASONS}
            label={getLocalization('lbl.variance-reason')}
            name="varianceReason"
            value={() => {
              const newVarianceReason = getVarianceReason(
                fixedDecimalCount(Number(actualPickupAmount)),
                fixedDecimalCount(Number(!isForeignTender ? currentFloat : foreignAmount)),
                fixedDecimalCount(Number(primaryCurrency ? defaultFloat : 0)),
                validationConfig.variance,
              );
              tender.varianceReason = newVarianceReason.VALUE;
              return newVarianceReason.LABEL;
            }}
            style={{ textAlign: 'initial' }}
          />
          <div className="pb3-sm">
            <VarianceReasonModal
              className="dialogModal"
              modalTitle={getLocalization('lbl.variance-reason-definitions')}
              additionalReasons={[
                {
                  item: getLocalization('lbl.variance-negative-till-balance'),
                  definition: getLocalization('lbl.variance-negative-till-balance-definition'),
                },
              ]}
            />
          </div>
        </div>
        {errorDisplay}
        <TextAreaStyled
          className='class="mt2-lg mb4-lg'
          rows={2}
          showCharacterCount
          maxLength={250}
          type="string"
          name="varianceExplanation"
          id={ID_VARIANCE_EXPLANATION}
          label={getLocalization('lbl.variance-explanation')}
          value={varianceExplanation}
          onChange={updateVarianceExplanation}
          style={{ textAlign: 'initial' }}
        />
      </div>
    </div>
  );
}

OpenTableItem.propTypes = {
  item: PropTypes.shape({
    index: PropTypes.number.isRequired,
    isDisabled: PropTypes.bool.isRequired,
    isOpenable: PropTypes.bool.isRequired,
  }).isRequired,
};

// eslint-disable-next-line react/prop-types, react/function-component-definition, prettier/prettier
const getOpenOrClose = ({ OpenIcon, CloseIcon, tenders }) => ({ index, isOpen }) => {
    if (!tenders[index].isEditable) return null;
    return isOpen ? <CloseIcon /> : <OpenIcon />;
  };

function TenderPickupsTable({ id, rowsAreOpenable, submit }) {
  const getLocalization = useGetLocalization();
  const { userLang } = useGetLangLocale();
  const direction = getDirection(userLang);
  const { tenders } = useTenders();

  const success = tenders.every((tender) => tender.validation.success);

  return (
    <div id={id}>
      <Table
        Open={OpenTableItem}
        columns={[
          {
            getClassName: () => (direction === 'rtl' ? 'ta-sm-r' : 'ta-sm-l'),
            header: getLocalization('lbl.tender-type'),
            headerClassName: direction === 'rtl' ? 'ta-sm-r' : 'ta-sm-l',
            processor: getTenderType(tenders),
            width: 5,
          },
          {
            getClassName: () => (direction === 'rtl' ? 'ta-sm-l' : 'ta-sm-r'),
            header: getLocalization('lbl.expected'),
            headerClassName: direction === 'rtl' ? 'ta-sm-l' : 'ta-sm-r',
            processor: getExpectedAmountForClosedItem(tenders),
            width: 3,
          },
          {
            getClassName: () => (direction === 'rtl' ? 'ta-sm-l' : 'ta-sm-r'),
            header: getLocalization('lbl.pickup'),
            headerClassName: direction === 'rtl' ? 'ta-sm-l' : 'ta-sm-r',
            processor: getPickupAmountForClosedItem(tenders),
            width: 3,
          },
          {
            getClassName: () => (direction === 'rtl' ? 'ta-sm-l' : 'ta-sm-r'),
            headerClassName: direction === 'rtl' ? 'ta-sm-l' : 'ta-sm-r',
            processor: getOpenOrClose({ CloseIcon: CaretUp, OpenIcon: CaretDown, tenders }),
            width: 1,
          },
        ]}
        data={tenders.map(({ index, isOpenable, isDisabled: tenderIsDisabled }) => ({
          index,
          isOpenable,
          isDisabled: tenderIsDisabled,
        }))}
        isBackgroundHighlightedOnOpen
        isItemAlternatingBackground={false}
        rowsAreOpenable={rowsAreOpenable}
        singleItemOpenAtATime
        subTitle={getLocalization('lbl.amounts')}
        title={getLocalization('lbl.amounts')}
        direction={direction}
      />
      <PickupDeclarationModal
        modalTitle={getLocalization('lbl.confirm-final-pickup-amounts')}
        success={success}
        submit={submit}
      />
    </div>
  );
}

TenderPickupsTable.propTypes = {
  id: PropTypes.string.isRequired,
  rowsAreOpenable: PropTypes.bool.isRequired,
  submit: PropTypes.func.isRequired,
};

export default TenderPickupsTable;
